import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { Form, Formik, useFormikContext } from 'formik';
import type { FormikComputedProps, FormikState } from 'formik';
import { useStore } from 'react-redux';
import { Col, Row } from 'reactstrap';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { onboardingUrl } from 'src/app/client/api';
import { acceptTos, accountCreateWithTransfer } from 'src/app/client/models/account';
import { IconListWithText } from 'src/app/components/Elements/IconListWithText';
import Spinner from 'src/app/components/Elements/Spinner';
import { Typography } from 'src/app/components/Elements/Typography';
import GenericError from 'src/app/components/Error/Generic';
import { TermsAndConditions } from 'src/app/components/Form/TermsAndConditions';
import TextInput from 'src/app/components/Form/TextInput';
import { PasswordInput } from 'src/app/components/Form/TextInput/Password';
import PhoneTextInput from 'src/app/components/Form/TextInput/Phone';
import { useUserCheckPassword } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { login as loginPath } from 'src/app/router/paths';
import {
  onboardingAccountNotifyTransfer,
  setMedicationSuccess,
  setOnboardingTransfersSuccess,
  transformOnboardingTransfers,
} from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import { extractErrorCodes } from 'src/app/utils';
import { theme } from 'src/styles/theme';

import { iconTextItems } from '../constants';
import { ImageTextHeader } from '../Sections/ImageTextHeader';

//
import type { PropsFromRedux } from './container';
import { ErrorLinkText } from './ErrorLinkText';
import {
  CaptionSection,
  CTAContent,
  FirstField,
  FormContainer,
  GetStartedButton,
  GetStartedContainer,
  GetStartedCTAWrapper,
  IconListContainer,
  InnerInputsSection,
  OtcPromoImage,
  PhoneInputPadding,
  PromoImageWrapper,
  PromoPageLink,
  RedeemOfferButton,
  Subheader,
  SubheaderContent,
} from './styles';
import { validation } from './validation';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  employeeBadge: string;
  zipCode: string;
}
interface PromoSignUpFormProps
  extends PropsFromRedux,
    FormikBag<Record<string, any>, FormValues>,
    FormikComputedProps<FormValues>,
    FormikState<FormValues> {
  buttonLabel: string;
  disclaimerLabel: string;
  header: string | ReactElement;
  subheader?: string | ReactElement;
  caption?: string | ReactElement;
  isEmployerOffering: boolean;
  offerSummary: string;
  promoCode: string;
  scrollId: string;
  isGetStartedCTA?: boolean;
}

export const Renderer = ({
  buttonLabel,
  disclaimerLabel,
  dispatchSetPromoCodeForOnboarding,
  dispatchSetPromoOnboardingHasExistingAccount,
  header,
  hideFields,
  isEmployerOffering,
  offerSummary,
  promoCode,
  scrollId,
  segmentTrack,
  shouldDisplayZipCode,
  subheader,
  caption,
  employeeDescription,
  isNonCreditPromo,
  isGetStartedCTA,
  onRedirectToOnboarding,

  checkZipCodeEligibility,
  dispatchSetPromoOnboardingZipCode,
  postOnboardingRedirect,
  redirectToPostOnboardingRedirect,
  validateData,
  isSinglePromoReferral,
  promoReferral,
}: PromoSignUpFormProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);
  const store = useStore();
  const state = store.getState();
  const { login } = useAuth();
  const dispatch = useDispatch();

  const { mutateAsync: checkPassword } = useUserCheckPassword();

  const [isLoading, setIsLoading] = useState(false);

  const handleLoginClick = () => {
    dispatchSetPromoOnboardingHasExistingAccount(true);
    dispatchSetPromoCodeForOnboarding(promoCode);
    segmentTrack(UNIFIED.selectExistingUserSignIn, { partnership: promoCode });
  };

  const items = iconTextItems({
    offerSummary,
    promoCode,
    employeeDescription,
  });

  const signInCTA = (
    <Typography variant="body" color="white">
      Already have an account?&nbsp;
      <PromoPageLink to={loginPath.toPath()} onClick={handleLoginClick}>
        <u>Sign in instead</u>
      </PromoPageLink>
      &nbsp;
      {!isNonCreditPromo && 'to get your discount.'}
    </Typography>
  );

  const getIconList = () => {
    return (
      <IconListContainer>
        {promoCode === 'sakara' ? <ImageTextHeader /> : <IconListWithText items={items} />}
      </IconListContainer>
    );
  };

  function ScrollToError() {
    const formik = useFormikContext();
    const submitting = formik?.isSubmitting;

    useEffect(() => {
      const el = document.querySelector('[aria-invalid="true"]');
      (el?.parentElement ?? el)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }, [submitting]);
    return null;
  }

  const handleGetStartedCTAOnClick = () => {
    onRedirectToOnboarding(promoCode);
  };

  const emailLabel = isEmployerOffering ? 'Personal email' : 'Email';

  if (isLoading) {
    return <Spinner centered />;
  }

  return (
    <div>
      <Typography
        variant={isMobile ? 'displayXL' : 'displayXXL'}
        color="white"
        marginBottom={subheader ? 's4' : 's7'}
        id={scrollId}
      >
        {header}
      </Typography>
      {subheader && (
        <SubheaderContent>
          <Subheader variant={'h3'} marginBottom="s7" color="white">
            {subheader}
          </Subheader>
        </SubheaderContent>
      )}
      {isGetStartedCTA && (
        <GetStartedContainer>
          {getIconList()}
          <CTAContent>
            <GetStartedCTAWrapper>
              <Typography variant={'overline'} color="white" marginBottom="s4">
                Get started in 15 seconds!
              </Typography>
              <GetStartedButton marginTop={'s1'} style="primary" onClick={handleGetStartedCTAOnClick}>
                Get Started
              </GetStartedButton>
              {signInCTA}
            </GetStartedCTAWrapper>
          </CTAContent>
        </GetStartedContainer>
      )}
      {!isGetStartedCTA && (
        <FormContainer>
          {promoCode === 'free-advil' ? (
            <PromoImageWrapper>
              <OtcPromoImage src="/public/free-advil.png" />
            </PromoImageWrapper>
          ) : (
            ''
          )}
          {getIconList()}
          <CTAContent>
            <Formik
              initialValues={validation.initialValues}
              validationSchema={validation.schema}
              validateOnChange={true}
              validateOnBlur={true}
              onSubmit={async (
                { email, password, firstName, lastName, phone, zipCode, employeeBadge },
                { setSubmitting, setErrors, setFieldError }
              ) => {
                setSubmitting(true);
                setIsLoading(true);
                segmentTrack(UNIFIED.submitOTCPromoSignup, { promotion_value: promoCode });
                segmentTrack(UNIFIED.submitPromoSignup, { promotion_value: promoCode });

                try {
                  const passwordCheckResp = await checkPassword(password);
                  if (!passwordCheckResp.isSafe) {
                    setFieldError(
                      'password',
                      'This password is not secure or is too common. Try increasing length or adding numbers and symbols.'
                    );
                    return;
                  }
                } catch (error) {
                  console.log(`Error checking password for email=${email}`);
                }

                const resp = await checkZipCodeEligibility(zipCode);
                const isDeliverable = resp.data?.isDeliverable;

                dispatchSetPromoOnboardingZipCode(parseInt(zipCode));

                if (isDeliverable) {
                  const formValues = {
                    email,
                    password,
                    firstName,
                    lastName,
                    phone,
                    zipCode,
                    birthdate: '01/01/1900',
                    signupSource: 'promotion',
                    referral: isSinglePromoReferral && promoReferral ? promoReferral : promoCode,
                    employeeBadge,
                  };

                  dispatchSetPromoOnboardingHasExistingAccount(false);
                  dispatchSetPromoCodeForOnboarding(promoCode);

                  try {
                    await validateData({
                      email: formValues.email,
                      password: formValues.password,
                      phone: formValues.phone,
                      signupSource: formValues.signupSource,
                      referral: formValues.referral,
                      employeeBadge: formValues.employeeBadge,
                    });

                    let response = {};
                    try {
                      const tryBlockResponse = await accountCreateWithTransfer(state.client, formValues); // OTC
                      response = tryBlockResponse;
                    } catch (error) {
                      const bffError = error?.error ?? '';
                      const api = onboardingUrl();
                      const trackErrorPayload = {
                        errorMessages: 'Onboarding Create Account With Transfer Failed',
                        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
                        api,
                        errorCode: extractErrorCodes(error),
                        source: window.location.href,
                      };
                      segmentTrack(UNIFIED.error, trackErrorPayload);
                    }
                    const transfers = transformOnboardingTransfers(formValues.medications);
                    dispatch(setMedicationSuccess(transfers));
                    dispatch(setOnboardingTransfersSuccess(transfers));
                    const { accountId, email, userId } = response.data;

                    await login(email, password);

                    await dispatch(onboardingAccountNotifyTransfer(accountId));
                    await acceptTos(state.client, { accountId, userId });

                    redirectToPostOnboardingRedirect(postOnboardingRedirect);
                  } catch (error: any) {
                    setErrors(
                      error.response?.data?.formErrors ?? {
                        _generic:
                          'Something isn’t quite right on our end... Please try again or contact the pharmacy for help.',
                      }
                    );
                  }
                } else {
                  setErrors({ zipCode: <ErrorLinkText /> });
                }

                setSubmitting(false);
                setIsLoading(false);
              }}
            >
              {({ isValid, isSubmitting }) => (
                <Form>
                  <GenericError />
                  <ScrollToError />
                  <InnerInputsSection>
                    {!hideFields && (
                      <Row>
                        <Col sm="12" lg="6">
                          <FirstField promoCode={promoCode}>
                            <TextInput
                              name="firstName"
                              label="First name"
                              autoComplete="given-name"
                              mediumContrast={true}
                              hasLightRedError
                              hasWhiteTextOnly
                            />
                          </FirstField>
                        </Col>
                        <Col sm="12" lg="6">
                          <TextInput
                            name="lastName"
                            label="Last name"
                            autoComplete="family-name"
                            mediumContrast={true}
                            hasLightRedError
                            hasWhiteTextOnly
                          />
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col>
                        <TextInput
                          name="email"
                          label={emailLabel}
                          mediumContrast={true}
                          hasLightRedError
                          hasWhiteTextOnly
                        />
                      </Col>
                    </Row>
                    {!hideFields && (
                      <Row>
                        <Col>
                          <PasswordInput
                            label="Password"
                            autoComplete="new-password"
                            mediumContrast
                            hasLightRedError
                            hasWhiteTextOnly
                          />
                        </Col>
                      </Row>
                    )}
                    {!hideFields && (
                      <Row>
                        <Col sm="12" lg="6">
                          <PhoneInputPadding>
                            <PhoneTextInput
                              name="phone"
                              label="Mobile number"
                              autoComplete="tel-national"
                              mediumContrast={true}
                              hasLightRedError
                              hasWhiteTextOnly
                            />
                          </PhoneInputPadding>
                        </Col>
                        {shouldDisplayZipCode && (
                          <Col sm="12" lg="6">
                            <TextInput
                              name="zipCode"
                              label="Delivery zip code"
                              autoComplete="postal-code"
                              maxLength={5}
                              mediumContrast={true}
                              hasLightRedError
                              hasWhiteTextOnly
                            />
                          </Col>
                        )}
                      </Row>
                    )}

                    <TermsAndConditions disclaimerText={disclaimerLabel} textColor={'white'} />
                    <RedeemOfferButton
                      style="primary"
                      type="submit"
                      marginTop={'s7'}
                      disabled={!isValid || isSubmitting}
                    >
                      {buttonLabel}
                    </RedeemOfferButton>
                    {signInCTA}
                  </InnerInputsSection>
                </Form>
              )}
            </Formik>
          </CTAContent>
        </FormContainer>
      )}
      {promoCode === 'sakara' ? null : <CaptionSection>{caption}</CaptionSection>}
    </div>
  );
};
